import React, { useState, useEffect } from 'react';
import { 
    Trophy, Calendar, Plus, Save, X, Loader2, 
    Search, ChevronRight, LayoutGrid, CheckCircle2,
    AlertCircle, Trash2, Edit2
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useToast } from '../../../context/ToastContext';
import { gameService } from '../../../services/gameService';
import { translateTotoTitle } from '../../../services/geminiService';
import * as footballService from '../../../services/footballService';
import { getTeamName } from '../../../services/teamService';
import { TotoRound, TotoMatch, Game } from '../../../types';
import { at } from '../../../i18nAdmin';
import { ConfirmModal } from '../../Common/ConfirmModal';

export const TotoTab: React.FC = () => {
    const { lang } = useLanguage();
    const { showToast } = useToast();
    
    const [rounds, setRounds] = useState<TotoRound[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [editingRound, setEditingRound] = useState<TotoRound | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [roundToDelete, setRoundToDelete] = useState<string | null>(null);
    const [isUpdatingResults, setIsUpdatingResults] = useState<string | null>(null);
    const [isTogglingStatus, setIsTogglingStatus] = useState<string | null>(null);

    // Form states
    const [titleKo, setTitleKo] = useState('');
    const [roundNumber, setRoundNumber] = useState(1);
    const [startAt, setStartAt] = useState('');
    const [endAt, setEndAt] = useState('');
    const [selectedMatches, setSelectedMatches] = useState<Omit<TotoMatch, 'id'>[]>([]);

    // Match Search states
    const [searchDate, setSearchDate] = useState(new Date(new Date().getTime() + 9 * 60 * 60 * 1000).toISOString().split('T')[0]);
    const [availableMatches, setAvailableMatches] = useState<Game[]>([]);
    const [isSearchingMatches, setIsSearchingMatches] = useState(false);

    useEffect(() => {
        loadRounds();
    }, []);

    const loadRounds = async () => {
        setIsLoading(true);
        try {
            const data = await gameService.getTotoRounds();
            setRounds(data);
            if (data.length > 0) {
                setRoundNumber(data[0].round_number + 1);
            }
        } catch (error) {
            showToast('error', 'Failed to load toto rounds');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearchMatches = async () => {
        setIsSearchingMatches(true);
        try {
            // 주요 리그 ID들
            const majorLeagues = ['1', '2', '3', '848', '5', '9', '10', '11', '45', '48', '81', '143', '137', '66', '39', '140', '135', '78', '61', '292', '293', '88', '94', '203', '253', '307', '40', '98', '71', '262', '128', '144', '179']; 
            const results = await Promise.all(
                majorLeagues.map(id => footballService.fetchGamesByLeagueAndDate(id, searchDate))
            );
            setAvailableMatches(results.flat());
        } catch (error) {
            showToast('error', 'Failed to search matches');
        } finally {
            setIsSearchingMatches(false);
        }
    };

    const toggleMatchSelection = (game: Game) => {
        const isSelected = selectedMatches.some(m => m.match_id === game.id);
        if (isSelected) {
            setSelectedMatches(selectedMatches.filter(m => m.match_id !== game.id));
        } else {
            if (selectedMatches.length >= 14) {
                showToast('error', 'Maximum 14 matches allowed');
                return;
            }
            setSelectedMatches([...selectedMatches, {
                round_id: '',
                match_id: game.id,
                order_index: selectedMatches.length + 1,
                home_team_name: game.homeTeam.name,
                home_team_logo: game.homeTeam.logo,
                home_team_id: game.homeTeam.id?.toString(),
                away_team_name: game.awayTeam.name,
                away_team_logo: game.awayTeam.logo,
                away_team_id: game.awayTeam.id?.toString(),
                match_time: `${game.dateLabel} ${game.time}`,
                status: game.status,
                league_id: game.leagueId || game.league
            }]);
        }
    };

    const handleSaveRound = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedMatches.length === 0) {
            showToast('error', 'Select at least one match');
            return;
        }

        setIsSaving(true);
        try {
            // Convert to ISO string (which is UTC) for DB storage
            const startAtUtc = startAt ? new Date(startAt).toISOString() : '';
            const endAtUtc = endAt ? new Date(endAt).toISOString() : '';

            if (editingRound) {
                // Update logic (simplified for now as only status is usually updated after creation)
                const success = await gameService.updateTotoRound(editingRound.id, {
                    title_ko: titleKo,
                    round_number: roundNumber,
                    start_at: startAtUtc,
                    end_at: endAtUtc
                });
                if (success) {
                    showToast('success', 'Toto round updated successfully');
                    setIsFormOpen(false);
                    loadRounds();
                    resetForm();
                }
            } else {
                // Translate title from Korean to other languages
                const translations = await translateTotoTitle(titleKo);

                const roundData = {
                    title_ko: titleKo,
                    title_en: translations?.title_en || null,
                    // title_ja: translations?.title_ja || null,
                    // title_es: translations?.title_es || null,
                    // title_fr: translations?.title_fr || null,
                    round_number: roundNumber,
                    start_at: startAtUtc,
                    end_at: endAtUtc,
                    status: 'open' as const
                };

                const newRound = await gameService.createTotoRound(roundData);
                if (newRound) {
                    const matchesWithRoundId = selectedMatches.map((m, idx) => ({
                        ...m,
                        round_id: newRound.id,
                        order_index: idx + 1
                    }));
                    await gameService.addTotoMatches(matchesWithRoundId);
                    showToast('success', 'Toto round created successfully');
                    setIsFormOpen(false);
                    loadRounds();
                    resetForm();
                }
            }
        } catch (error) {
            showToast('error', 'Failed to save toto round');
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditRound = async (round: TotoRound) => {
        setIsLoading(true);
        try {
            const matchData = await gameService.getTotoMatches(round.id);
            setEditingRound(round);
            setTitleKo(round.title_ko);
            setRoundNumber(round.round_number);
            
            // Format UTC date to KST for datetime-local input
            const formatForInput = (utcDate: string) => {
                const date = new Date(utcDate);
                const offset = date.getTimezoneOffset() * 60000;
                const localISODate = new Date(date.getTime() - offset).toISOString().slice(0, 16);
                return localISODate;
            };

            setStartAt(formatForInput(round.start_at));
            setEndAt(formatForInput(round.end_at));
            setSelectedMatches(matchData);
            setIsFormOpen(true);
            setTimeout(() => setIsAnimating(true), 50);
        } catch (error) {
            showToast('error', 'Failed to load round details');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAutoUpdateResults = async (roundId: string) => {
        setIsUpdatingResults(roundId);
        try {
            const matches = await gameService.getTotoMatches(roundId);
            let updatedCount = 0;

            await Promise.all(matches.map(async (m) => {
                if (m.result) return; // Already has result

                try {
                    const resultData = await footballService.fetchMatchResultOnly(m.match_id);
                    if (resultData && resultData.status === 'Finished') {
                        const homeScore = resultData.goals?.home ?? 0;
                        const awayScore = resultData.goals?.away ?? 0;
                        const result = homeScore > awayScore ? '1' : homeScore < awayScore ? '2' : 'X';
                        
                        const success = await gameService.updateTotoMatchResult(m.id, result as '1' | 'X' | '2');
                        if (success) updatedCount++;
                    }
                } catch (e) {
                    console.error(`Failed to update result for match ${m.match_id}:`, e);
                }
            }));

            showToast('success', `${updatedCount} matches updated with results`);
            loadRounds();
        } catch (error) {
            showToast('error', 'Failed to auto update results');
        } finally {
            setIsUpdatingResults(null);
        }
    };

    const handleToggleStatus = async (round: TotoRound) => {
        setIsTogglingStatus(round.id);
        try {
            const newStatus = round.status === 'open' ? 'closed' : 'open';
            const success = await gameService.updateTotoRound(round.id, {
                status: newStatus
            });
            if (success) {
                showToast('success', `회차 상태가 ${newStatus === 'open' ? '오픈' : '마감'}으로 변경되었습니다.`);
                loadRounds();
            }
        } catch (error) {
            showToast('error', '상태 변경에 실패했습니다.');
        } finally {
            setIsTogglingStatus(null);
        }
    };

    const handleDeleteRound = async () => {
        if (!roundToDelete) return;
        try {
            const success = await gameService.deleteTotoRound(roundToDelete);
            if (success) {
                showToast('success', 'Toto round deleted successfully');
                loadRounds();
            } else {
                showToast('error', 'Failed to delete toto round');
            }
        } catch (error) {
            showToast('error', 'An error occurred during deletion');
        } finally {
            setRoundToDelete(null);
        }
    };

    const resetForm = () => {
        setTitleKo('');
        setStartAt('');
        setEndAt('');
        setSelectedMatches([]);
        setEditingRound(null);
    };

    const handleCloseModal = () => {
        setIsAnimating(false);
        setTimeout(() => {
            setIsFormOpen(false);
            resetForm();
        }, 500);
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase flex items-center gap-3">
                        <Trophy className="text-amber-500" size={32} />
                        축구 토토 관리
                    </h1>
                    <p className="text-sm font-medium text-slate-500 mt-1">
                        승무패 회차 및 경기를 관리하고 운영합니다.
                    </p>
                </div>
                <button 
                    onClick={() => {
                        resetForm();
                        setIsFormOpen(true);
                        setTimeout(() => setIsAnimating(true), 50);
                    }}
                    className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest md:hover:bg-black transition-all active:scale-95 shadow-xl shadow-slate-200"
                >
                    <Plus size={16} strokeWidth={3} />
                    새 회차 생성
                </button>
            </div>

            {/* Rounds List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {isLoading ? (
                    <div className="col-span-full py-20 flex flex-col items-center justify-center gap-3">
                        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Loading Rounds...</p>
                    </div>
                ) : rounds.length === 0 ? (
                    <div className="col-span-full py-20 bg-white rounded-[32px] border border-dashed border-slate-200 flex flex-col items-center justify-center text-center">
                        <Trophy size={48} className="text-slate-200 mb-4" />
                        <p className="text-sm font-bold text-slate-400">등록된 토토 회차가 없습니다.</p>
                    </div>
                ) : (
                    rounds.map(round => (
                        <div key={round.id} className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-shadow group">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <span className="text-[10px] font-black px-2.5 py-1 bg-blue-50 text-blue-600 rounded-lg uppercase tracking-widest mb-2 inline-block">
                                        제 {round.round_number}회차
                                    </span>
                                    <h3 className="text-lg font-black text-slate-900 leading-tight group-hover:text-blue-600 transition-colors">
                                        {round[`title_${lang}` as keyof TotoRound] || round.title_ko}
                                    </h3>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className={`px-2 py-1 rounded-md text-[9px] font-black uppercase tracking-widest ${
                                        round.status === 'open' ? 'bg-green-100 text-green-600' :
                                        round.status === 'closed' ? 'bg-slate-100 text-slate-600' :
                                        'bg-blue-100 text-blue-600'
                                    }`}>
                                        {round.status}
                                    </div>
                                    <button
                                        onClick={() => handleToggleStatus(round)}
                                        disabled={isTogglingStatus === round.id}
                                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                            round.status === 'open' ? 'bg-green-500' : 'bg-slate-200'
                                        } ${isTogglingStatus === round.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <span
                                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                round.status === 'open' ? 'translate-x-4' : 'translate-x-0'
                                            }`}
                                        />
                                    </button>
                                </div>
                            </div>
                            
                            <div className="space-y-2 mb-6">
                                <div className="flex items-center gap-2 text-xs font-bold text-slate-400">
                                    <Calendar size={14} />
                                    <span>시작: {new Date(round.start_at).toLocaleString()}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs font-bold text-slate-400">
                                    <Calendar size={14} />
                                    <span>종료: {new Date(round.end_at).toLocaleString()}</span>
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <button 
                                    onClick={() => handleAutoUpdateResults(round.id)}
                                    disabled={isUpdatingResults === round.id || round.status === 'closed'}
                                    className="w-full py-2.5 bg-blue-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-blue-100"
                                >
                                    {isUpdatingResults === round.id ? (
                                        <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                        <CheckCircle2 size={14} />
                                    )}
                                    결과 자동 등록
                                </button>
                                <div className="flex gap-2">
                                    <button 
                                        onClick={() => handleEditRound(round)}
                                        className="flex-1 py-2.5 bg-slate-50 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-colors"
                                    >
                                        상세보기
                                    </button>
                                    <button 
                                        onClick={() => setRoundToDelete(round.id)}
                                        className="px-3 py-2.5 bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-100 transition-colors"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Confirm Delete Modal */}
            <ConfirmModal 
                isOpen={!!roundToDelete}
                onClose={() => setRoundToDelete(null)}
                onConfirm={handleDeleteRound}
                title="토토 회차 삭제"
                description="정말로 이 회차를 삭제하시겠습니까? 관련 모든 경기 데이터가 함께 삭제됩니다."
                confirmLabel="삭제"
                cancelLabel="취소"
            />

            {/* Create/Edit Modal */}
            {isFormOpen && (
                <div className={`fixed inset-0 z-[600] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 !m-0 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={handleCloseModal}></div>
                    <div className={`relative bg-slate-50 w-full max-w-4xl h-[90vh] md:max-h-[90vh] rounded-t-[40px] md:rounded-[40px] shadow-2xl overflow-hidden flex flex-col transition-transform duration-700 ease-[0.32,0.72,0,1] ${
                        isAnimating 
                            ? 'translate-y-0 md:scale-100' 
                            : 'translate-y-full md:translate-y-12 md:scale-95'
                    }`}>
                        <header className="p-8 bg-white border-b border-slate-100 flex items-center justify-between shrink-0">
                            <div>
                                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                                    {editingRound ? '토토 회차 정보 수정' : '새 토토 회차 생성'}
                                </h3>
                                <p className="text-xs font-bold text-slate-400 mt-0.5">운영 기간 및 대상 경기를 선택하세요.</p>
                            </div>
                            <button onClick={handleCloseModal} className="p-2 text-slate-300 hover:text-slate-900 transition-colors">
                                <X size={24} />
                            </button>
                        </header>

                        <div className="flex-1 overflow-y-auto p-8 space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">회차 제목 (한국어)</label>
                                    <input 
                                        className="w-full bg-white border border-slate-200 rounded-2xl py-3.5 px-5 text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                                        placeholder="ex) 2024년 2월 3주차 승무패"
                                        value={titleKo}
                                        onChange={e => setTitleKo(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">회차 번호</label>
                                    <input 
                                        type="number"
                                        className="w-full bg-white border border-slate-200 rounded-2xl py-3.5 px-5 text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                                        value={roundNumber}
                                        onChange={e => setRoundNumber(parseInt(e.target.value))}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">시작 일시</label>
                                    <input 
                                        type="datetime-local"
                                        className="w-full bg-white border border-slate-200 rounded-2xl py-3.5 px-5 text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                                        value={startAt}
                                        onChange={e => setStartAt(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">종료 일시</label>
                                    <input 
                                        type="datetime-local"
                                        className="w-full bg-white border border-slate-200 rounded-2xl py-3.5 px-5 text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 transition-all"
                                        value={endAt}
                                        onChange={e => setEndAt(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                                    <h4 className="text-sm font-black text-slate-900 uppercase tracking-tight flex items-center gap-2">
                                        <LayoutGrid size={18} className="text-blue-500" />
                                        경기 선택 ({selectedMatches.length}/14)
                                    </h4>
                                    <div className="flex items-center gap-3">
                                        <input 
                                            type="date"
                                            className="bg-white border border-slate-200 rounded-xl py-2 px-3 text-xs font-bold outline-none"
                                            value={searchDate}
                                            onChange={e => setSearchDate(e.target.value)}
                                        />
                                        <button 
                                            onClick={handleSearchMatches}
                                            disabled={isSearchingMatches}
                                            className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
                                        >
                                            {isSearchingMatches ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-3">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">검색 결과</p>
                                        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden max-h-[300px] overflow-y-auto custom-scrollbar">
                                            {availableMatches.length === 0 ? (
                                                <div className="p-8 text-center text-slate-300">
                                                    <Search size={32} className="mx-auto mb-2 opacity-20" />
                                                    <p className="text-xs font-bold">날짜를 선택하고 검색하세요.</p>
                                                </div>
                                            ) : (
                                                availableMatches.map(game => {
                                                    const isSelected = selectedMatches.some(m => m.match_id === game.id);
                                                    return (
                                                        <div 
                                                            key={game.id}
                                                            onClick={() => toggleMatchSelection(game)}
                                                            className={`p-4 flex items-center justify-between cursor-pointer border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors ${isSelected ? 'bg-blue-50/50' : ''}`}
                                                        >
                                                            <div className="flex items-center gap-3 flex-1">
                                                                <div className="text-center min-w-[60px]">
                                                                    <p className="text-[10px] font-black text-slate-400">{game.time}</p>
                                                                    <p className="text-[8px] font-bold text-slate-300 truncate w-16">{game.leagueName}</p>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs font-bold text-slate-700">
                                                                        {getTeamName(game.homeTeam.id, lang, game.homeTeam.name)}
                                                                    </span>
                                                                    <span className="text-[10px] font-black text-slate-300">VS</span>
                                                                    <span className="text-xs font-bold text-slate-700">
                                                                        {getTeamName(game.awayTeam.id, lang, game.awayTeam.name)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            {isSelected ? (
                                                                <CheckCircle2 size={18} className="text-blue-500" />
                                                            ) : (
                                                                <Plus size={18} className="text-slate-200" />
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">선택된 경기</p>
                                        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden max-h-[300px] overflow-y-auto custom-scrollbar">
                                            {selectedMatches.length === 0 ? (
                                                <div className="p-8 text-center text-slate-300">
                                                    <AlertCircle size={32} className="mx-auto mb-2 opacity-20" />
                                                    <p className="text-xs font-bold">선택된 경기가 없습니다.</p>
                                                </div>
                                            ) : (
                                                selectedMatches.map((m, idx) => (
                                                    <div key={m.match_id} className="p-4 flex items-center gap-4 border-b border-slate-50 last:border-0">
                                                        <span className="text-xs font-black text-blue-600 w-4">{idx + 1}</span>
                                                        <div className="flex-1 flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-xs font-bold text-slate-700">
                                                                    {getTeamName(m.home_team_id, lang, m.home_team_name)}
                                                                </span>
                                                                <span className="text-[10px] font-black text-slate-300">VS</span>
                                                                <span className="text-xs font-bold text-slate-700">
                                                                    {getTeamName(m.away_team_id, lang, m.away_team_name)}
                                                                </span>
                                                            </div>
                                                            <button 
                                                                onClick={() => setSelectedMatches(selectedMatches.filter(item => item.match_id !== m.match_id))}
                                                                className="text-slate-300 hover:text-rose-500 transition-colors"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <footer className="p-8 bg-white border-t border-slate-100 flex items-center justify-end gap-3 shrink-0">
                            <button 
                                onClick={handleCloseModal}
                                className="px-6 py-3.5 text-slate-500 text-xs font-black uppercase tracking-widest hover:text-slate-900 transition-colors"
                            >
                                취소
                            </button>
                            <button 
                                onClick={handleSaveRound}
                                disabled={isSaving || !titleKo || selectedMatches.length === 0}
                                className="px-8 py-3.5 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-black transition-all active:scale-95 disabled:opacity-50 shadow-xl shadow-slate-200 flex items-center gap-2"
                            >
                                {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                회차 저장하기
                            </button>
                        </footer>
                    </div>
                </div>
            )}
        </div>
    );
};
